import API_ENDPOINTS from '../config/api'
import type {
  CrossTextAlignmentCandidate,
  CrossTextAlignmentRun,
  CrossTextAlignmentTier,
  CrossTextSourceSegment,
  CrossTextReviewStatus,
} from '../lib/crossTextTraditionAlignmentReview'

export interface WorkbenchCrossTextAlignmentTextInput {
  textId: string
  label: string
  contextLabel?: string | null
  content: string
  segments?: CrossTextSourceSegment[]
}

export interface RequestWorkbenchCrossTextAlignmentPreviewInput {
  clientRunId?: string | null
  texts: WorkbenchCrossTextAlignmentTextInput[]
  sourceTextId: string
  targetTextIds: string[]
  scorer?: string
  rerankModelId?: string | null
  queryPrompt?: string | null
  windowSizes?: number[]
  maxCandidatesPerSource?: number
  maxGlobalCandidates?: number
  minScore?: number
  signal?: AbortSignal
}

const mapCandidate = (candidate: {
  candidate_id: string
  source_text_id: string
  source_segment_start: number
  source_segment_end: number
  source_text: string
  target_text_id: string
  target_label: string
  target_context_label?: string | null
  target_segment_start: number
  target_segment_end: number
  target_text: string
  target_alignment_start?: number | null
  target_alignment_end?: number | null
  score: number
  score_margin?: number
  rank: number
  tier: CrossTextAlignmentTier
  perspective_mode?: string
  review_status?: CrossTextReviewStatus
  rerank_model_id?: string | null
  rerank_verdict?: string | null
  rerank_confidence?: number | null
  rerank_reason?: string | null
}): CrossTextAlignmentCandidate => ({
  candidateId: candidate.candidate_id,
  sourceTextId: candidate.source_text_id,
  sourceSegmentStart: candidate.source_segment_start,
  sourceSegmentEnd: candidate.source_segment_end,
  sourceText: candidate.source_text,
  targetTextId: candidate.target_text_id,
  targetLabel: candidate.target_label,
  targetContextLabel: candidate.target_context_label || null,
  targetSegmentStart: candidate.target_segment_start,
  targetSegmentEnd: candidate.target_segment_end,
  targetText: candidate.target_text,
  targetAlignmentStart: candidate.target_alignment_start ?? null,
  targetAlignmentEnd: candidate.target_alignment_end ?? null,
  score: candidate.score,
  scoreMargin: candidate.score_margin ?? 0,
  rank: candidate.rank,
  tier: candidate.tier,
  perspectiveMode: candidate.perspective_mode || 'same-perspective',
  reviewStatus: candidate.review_status || 'pending',
  rerankModelId: candidate.rerank_model_id || null,
  rerankVerdict: candidate.rerank_verdict || null,
  rerankConfidence: candidate.rerank_confidence ?? null,
  rerankReason: candidate.rerank_reason || null,
})

export const requestWorkbenchCrossTextAlignmentPreview = async (
  input: RequestWorkbenchCrossTextAlignmentPreviewInput,
): Promise<CrossTextAlignmentRun> => {
  const response = await fetch(API_ENDPOINTS.workbenchCrossTextAlignment.preview, {
    method: 'POST',
    signal: input.signal,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_run_id: input.clientRunId || null,
      texts: input.texts.map(text => ({
        text_id: text.textId,
        label: text.label,
        context_label: text.contextLabel || null,
        content: text.content,
        segments: text.segments?.map(segment => ({
          index: segment.index,
          text: segment.text,
          folio_markers_before: segment.folioMarkersBefore?.map(marker => ({
            marker: marker.marker,
          })) || [],
        })),
      })),
      source_text_id: input.sourceTextId,
      target_text_ids: input.targetTextIds,
      scorer: input.scorer || 'tiered-gemini-windowed',
      rerank_model_id: input.rerankModelId ?? null,
      query_prompt: input.queryPrompt ?? null,
      window_sizes: input.windowSizes || [1, 2, 3, 4],
      max_candidates_per_source: input.maxCandidatesPerSource || 8,
      max_global_candidates: input.maxGlobalCandidates ?? 1000,
      min_score: input.minScore ?? 0.0,
    }),
  })

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    let message = detail || 'Failed to build cross-text alignment preview'
    try {
      const payload = JSON.parse(detail) as { detail?: string }
      message = payload.detail || message
    } catch {
      // Preserve the raw server message when it is not JSON.
    }
    throw new Error(message)
  }

  const payload = await response.json() as {
    preview_id: string
    model_id: string
    rerank_model_id?: string | null
    source_text: {
      text_id: string
      label: string
      context_label?: string | null
      segments?: Array<{
        index: number
        text: string
        folio_markers_before?: Array<{ marker: string }>
      }>
    }
    candidates?: Array<Parameters<typeof mapCandidate>[0]>
    diagnostics?: Record<string, unknown>
  }

  return {
    previewId: payload.preview_id,
    modelId: payload.model_id,
    rerankModelId: payload.rerank_model_id || null,
    sourceText: {
      textId: payload.source_text.text_id,
      label: payload.source_text.label,
      contextLabel: payload.source_text.context_label || null,
      segments: (payload.source_text.segments || []).map(segment => ({
        index: segment.index,
        text: segment.text,
        folioMarkersBefore: (segment.folio_markers_before || [])
          .map(marker => ({ marker: marker.marker }))
          .filter(marker => marker.marker.trim().length > 0),
      })),
    },
    candidates: (payload.candidates || []).map(mapCandidate),
    diagnostics: payload.diagnostics || {},
  }
}

export const cancelWorkbenchCrossTextAlignmentPreview = async (runId: string): Promise<void> => {
  if (!runId.trim()) {
    return
  }
  const response = await fetch(API_ENDPOINTS.workbenchCrossTextAlignment.cancelRun(runId), {
    method: 'POST',
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    let message = detail || 'Failed to cancel cross-text alignment preview'
    try {
      const payload = JSON.parse(detail) as { detail?: string }
      message = payload.detail || message
    } catch {
      // Preserve the raw server message when it is not JSON.
    }
    throw new Error(message)
  }
}
